import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import { EventImpl } from '@fullcalendar/core/internal';
import FullCalendar from '@fullcalendar/react';
import { StoreOrderingForm } from 'api/ordering-service/ordering-service.types';
import i18n from 'app-i18n';
import { DateTime } from 'luxon';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DUMMY_EVENT_ID,
  SetHoursFormDataProps,
  createDummyEvent,
} from '../location-hours.utils';
import {
  convertEventApiToRecurring,
  convertNormalHoursToEventInput,
  convertSetNormalHoursFormDataToEventSource,
  filterNormalHoursOverlappedEvents,
  recurringEventsToNormalHours,
} from './normal-hours.utils';

export type NormalHoursType = {
  normalHoursRef: MutableRefObject<FullCalendar>;
  normalEvents: EventInput[];
  addEvents: (setHourFormData: SetHoursFormDataProps) => void;
  addDummyEvent: (start: DateTime, end: DateTime) => EventInput;
  removeDummyEvent: () => void;
  eventsSet: (events: EventApi[]) => void;
  handleCalendarViewChange: (view: 'list' | 'week') => void;
  convertEventsToNormalHours: (
    normalEvents: EventInput[],
  ) => Partial<StoreOrderingForm>;
};

type UseNormalHoursProps = {
  storeOrderingData: StoreOrderingForm;
};

// Define custom hook for handling normal hours
export const useNormalHours = ({ storeOrderingData }: UseNormalHoursProps) => {
  const [normalEvents, setNormalEvents] = useState<EventInput[]>();
  const [normalCalendarInitialLoad, setNormalCalendarInitialLoad] =
    useState(false);
  const { pickupOrderHours, deliveryOrderHours } = storeOrderingData;

  const normalHoursRef = useRef<FullCalendar>();
  const normalHoursCalendarApi = normalHoursRef.current?.getApi();

  const initialEvents = useMemo(
    () => [
      ...convertNormalHoursToEventInput(pickupOrderHours, {
        orderType: 'PICKUP',
        eventType: 'LocationHours',
      }),
      ...convertNormalHoursToEventInput(deliveryOrderHours, {
        orderType: 'DELIVERY',
        eventType: 'LocationHours',
      }),
    ],
    [pickupOrderHours, deliveryOrderHours],
  );

  const convertEventsToNormalHours = useCallback(
    (normalEvents: EventInput[]): Partial<StoreOrderingForm> => {
      return {
        pickupOrderHours: normalEvents
          .filter(event => event.extendedProps.orderType === 'PICKUP')
          .reduce(recurringEventsToNormalHours, []),
        deliveryOrderHours: normalEvents
          .filter(event => event.extendedProps.orderType === 'DELIVERY')
          .reduce(recurringEventsToNormalHours, []),
      };
    },
    [],
  );

  const addDummyEvent = useCallback(
    (start: DateTime, end: DateTime) => {
      const dummyEvent = createDummyEvent(
        start,
        end,
        i18n.t('LocationHours.eventTitles.pickup'),
        'PICKUP',
      );
      normalHoursCalendarApi?.addEvent(dummyEvent);
      return dummyEvent;
    },
    [normalHoursCalendarApi],
  );

  const removeDummyEvent = useCallback(() => {
    const dummyEvent = normalHoursCalendarApi?.getEventById(DUMMY_EVENT_ID);
    if (dummyEvent) {
      dummyEvent.remove();
    }
  }, [normalHoursCalendarApi]);

  const addEvents = useCallback(
    (setHourFormData: SetHoursFormDataProps) => {
      const newEvents =
        convertSetNormalHoursFormDataToEventSource(setHourFormData);

      const overlappedEvents = filterNormalHoursOverlappedEvents(
        setHourFormData,
        normalEvents,
      );

      // Remove overlapped events from the calendar
      overlappedEvents.forEach(event => {
        normalHoursCalendarApi?.getEventById(event.id).remove();
      });

      // Remove existing event if updating
      if (setHourFormData.eventId) {
        normalHoursCalendarApi?.getEventById(setHourFormData.eventId).remove();
      }

      // Add new events to the calendar
      normalHoursCalendarApi?.addEventSource(newEvents);
    },
    [normalEvents, normalHoursCalendarApi],
  );

  const eventsSet = useCallback((events: EventImpl[]) => {
    // Use reduce to create a map with the last occurrence of each event by unique identifier
    const eventMap = events.reduce((map, event) => {
      map.set(event.id, event);
      return map;
    }, new Map());

    // Convert the map values to an array, map to the required format, and set the unique events
    setNormalEvents(
      Array.from(eventMap.values()).map(convertEventApiToRecurring),
    );
  }, []);

  const handleCalendarViewChange = useCallback(
    (view: 'list' | 'week') => {
      switch (view) {
        case 'week':
          normalHoursCalendarApi?.changeView('timeGridWeek');
          break;
        default:
          normalHoursCalendarApi?.changeView('listWeek');
      }
    },
    [normalHoursCalendarApi],
  );

  useEffect(() => {
    if (normalHoursCalendarApi && !normalCalendarInitialLoad) {
      normalHoursCalendarApi.removeAllEvents();
      normalHoursCalendarApi.addEventSource(initialEvents);
      setNormalEvents(initialEvents); // Set initial events to normalEvents state
      setNormalCalendarInitialLoad(true); // Set initial load flag to true
    }
  }, [initialEvents, normalHoursCalendarApi, normalCalendarInitialLoad]);

  return {
    normalHoursRef,
    normalEvents,
    addEvents,
    eventsSet,
    addDummyEvent,
    removeDummyEvent,
    handleCalendarViewChange,
    convertEventsToNormalHours,
  };
};
